import { ChevronDown, ChevronUp } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CrewListRow, CrewListRowCard } from "@/components/crew/crew-list-row"
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import type { CrewMember, CrewSort, CrewSortField } from "@/types/crew"

/**
 * OKS-25 F8 — Crew Roster table.
 *
 * Two render modes per UX Spec §Responsive:
 *   - **Desktop ≥640px (`useIsMobile() === false`)** — shadcn `Table`
 *     with sortable column headers. Joined + Followers headers are
 *     buttons; `aria-sort` reflects the current sort prop. Other
 *     headers are plain `<th>` (Creator + Engagement + Location +
 *     Tags are all v1 non-sortable).
 *   - **Mobile <640px (`useIsMobile() === true`)** — stacked card
 *     list. Hides Location / Tags / Joined columns per UX Spec
 *     (disclosure pattern lands with OKS-23). Sort UI moves to the
 *     filter rail's `crew-list-sort` Select (rendered by F6).
 *
 * Locked testids: `crew-list-table` (region), `crew-list-row` (every
 * row), `crew-list-row-{id}`, `crew-list-sort-joined`,
 * `crew-list-sort-followers`.
 */
interface CrewListTableProps {
  members: CrewMember[]
  sort: CrewSort
  onSortChange: (sort: CrewSort) => void
}

export function CrewListTable({
  members,
  sort,
  onSortChange,
}: CrewListTableProps) {
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  if (isMobile) {
    return (
      <div data-testid="crew-list-table" className="flex flex-col gap-3">
        {members.map((member) => (
          <CrewListRowCard key={member.id} member={member} />
        ))}
      </div>
    )
  }

  return (
    <div data-testid="crew-list-table" className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">
              {t("crew.list.column.creator", "Creator")}
            </TableHead>
            <TableHead
              scope="col"
              aria-sort={ariaSortFor(sort, "follower_count")}
            >
              <SortableHeader
                testId="crew-list-sort-followers"
                field="follower_count"
                label={t("crew.list.column.followers", "Followers")}
                sort={sort}
                onSortChange={onSortChange}
              />
            </TableHead>
            <TableHead scope="col">
              {t("crew.list.column.engagement", "Engagement")}
            </TableHead>
            <TableHead scope="col">
              {t("crew.list.column.location", "Location")}
            </TableHead>
            <TableHead scope="col">
              {t("crew.list.column.tags", "Tags")}
            </TableHead>
            <TableHead scope="col" aria-sort={ariaSortFor(sort, "joined_at")}>
              <SortableHeader
                testId="crew-list-sort-joined"
                field="joined_at"
                label={t("crew.list.column.joined", "Joined")}
                sort={sort}
                onSortChange={onSortChange}
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <CrewListRow key={member.id} member={member} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

// ─── Sortable column header button ────────────────────────────────

/**
 * Compute the `aria-sort` value for a `<TableHead scope="col">` given
 * the current sort state and which field this column represents.
 * Lives on the `<th>` (NOT the inner `<button>`) so NVDA + JAWS
 * announce the sort state when the user navigates the table by
 * column — putting it on the button only surfaces the state once
 * the user tabs into the button itself, missing the SR column-walk.
 *
 * Per UX review of F10.
 */
export function ariaSortFor(
  sort: CrewSort,
  field: CrewSortField,
): "ascending" | "descending" | "none" {
  if (sort.field !== field) return "none"
  return sort.dir === "asc" ? "ascending" : "descending"
}

interface SortableHeaderProps {
  testId: string
  field: CrewSortField
  label: string
  sort: CrewSort
  onSortChange: (sort: CrewSort) => void
}

/**
 * Clickable column-header sort button. Click toggles asc/desc; first
 * click on a non-active column lands `desc` (matches OKS-17 muscle
 * memory + UX Spec default-sort idiom — "newest first" is the
 * dominant intent).
 *
 * `aria-sort` is set on the parent `<TableHead scope="col">` via
 * `ariaSortFor()` for SR column-walk announcement (NOT on this
 * button — see `ariaSortFor` docstring).
 *
 * Locked testid: `crew-list-sort-{joined,followers}`.
 */
function SortableHeader({
  testId,
  field,
  label,
  sort,
  onSortChange,
}: SortableHeaderProps) {
  const isActive = sort.field === field
  const Icon = isActive && sort.dir === "asc" ? ChevronUp : ChevronDown

  function handleClick() {
    if (!isActive) {
      onSortChange({ field, dir: "desc" })
      return
    }
    onSortChange({ field, dir: sort.dir === "asc" ? "desc" : "asc" })
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      className={cn(
        "-m-2 inline-flex items-center gap-1 rounded p-2",
        "hover:text-foreground focus-visible:ring-ring focus-visible:ring-2",
        isActive ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {label}
      <Icon
        className={cn("size-3", isActive ? "opacity-100" : "opacity-50")}
        aria-hidden="true"
      />
    </button>
  )
}
